import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { subscribeToUserCollection, addUserDoc, updateUserDoc, deleteUserDoc } from '../utils/userDataHelper';
import { num } from './utils/costCalculations';
import Breadcrumbs from '../Components/Breadcrumbs';
import Footer from '../Components/Footer';
import './labcss/notifications.css';

/**
 * Notifications - Manage reminders and alerts
 * Route: /lab/notifications
 * 
 * Features:
 * - Auto-generated alerts (low stock, curing complete, expiring soon)
 * - Custom reminders with due dates
 * - Notification preferences
 * - Browser push notifications (with permission)
 */

// Notification types with icons and colors
const NOTIFICATION_TYPES = {
  low_stock: { icon: '📦', color: '#ff9800', label: 'Low Stock' },
  curing_ready: { icon: '✅', color: '#4caf50', label: 'Curing Complete' },
  curing_soon: { icon: '⏰', color: '#2196f3', label: 'Curing Soon' },
  expiring: { icon: '⚠️', color: '#f44336', label: 'Expiring Soon' },
  reminder: { icon: '🔔', color: '#9c27b0', label: 'Reminder' },
  custom: { icon: '📝', color: '#607d8b', label: 'Custom' },
};

function Notifications() {
  const [ingredients, setIngredients] = useState([]);
  const [batches, setBatches] = useState([]);
  const [customReminders, setCustomReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddReminder, setShowAddReminder] = useState(false);
  const [newReminder, setNewReminder] = useState({ title: '', description: '', dueDate: '', priority: 'medium' });
  const [dismissedAlerts, setDismissedAlerts] = useState(() => {
    const saved = localStorage.getItem('dismissedAlerts');
    return saved ? JSON.parse(saved) : [];
  });
  const [notificationPermission, setNotificationPermission] = useState('default');

  // Check browser notification permission
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  }, []);

  // Request notification permission
  const requestPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      if (permission === 'granted') {
        new Notification('Soap Lab Notifications Enabled', {
          body: 'You will now receive alerts for important events.',
          icon: '🧪',
        });
      }
    }
  };

  // Load data
  useEffect(() => {
    const unsubs = [];

    try {
      unsubs.push(subscribeToUserCollection('ingredients', (data) => {
        setIngredients(data);
      }));

      unsubs.push(subscribeToUserCollection('batches', (data) => {
        setBatches(data);
      }));

      unsubs.push(subscribeToUserCollection('reminders', (data) => {
        // Sort by dueDate
        const sorted = [...data].sort((a, b) => {
          if (!a.dueDate) return 1;
          if (!b.dueDate) return -1;
          return new Date(a.dueDate) - new Date(b.dueDate);
        });
        setCustomReminders(sorted);
        setLoading(false);
      }));
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }

    return () => unsubs.forEach(u => u());
  }, []);

  // Save dismissed alerts to localStorage
  useEffect(() => {
    localStorage.setItem('dismissedAlerts', JSON.stringify(dismissedAlerts));
  }, [dismissedAlerts]);

  // Generate automatic alerts
  const autoAlerts = useMemo(() => {
    const alerts = [];
    const today = new Date();

    // Low stock alerts
    ingredients.forEach(ing => {
      const stock = num(ing.stockGrams || ing.stock, 0);
      const minStock = num(ing.minStockGrams || ing.minStock || 100, 100);
      if (stock < minStock) {
        const alertId = `low_stock_${ing.id}`;
        if (!dismissedAlerts.includes(alertId)) {
          alerts.push({
            id: alertId,
            type: 'low_stock',
            title: `Low Stock: ${ing.name}`,
            description: `Only ${stock}g remaining (minimum: ${minStock}g)`,
            severity: stock < minStock * 0.5 ? 'high' : 'medium',
            itemId: ing.id,
            autoGenerated: true,
          });
        }
      }
    });

    // Batch curing alerts
    batches.forEach(batch => {
      if (batch.status === 'curing' && batch.madeDate) {
        const madeDate = new Date(batch.madeDate);
        const readyDate = new Date(madeDate.getTime() + 42 * 24 * 60 * 60 * 1000); // 6 weeks
        const daysLeft = Math.ceil((readyDate - today) / (1000 * 60 * 60 * 24));

        if (daysLeft <= 0) {
          const alertId = `curing_ready_${batch.id}`;
          if (!dismissedAlerts.includes(alertId)) {
            alerts.push({
              id: alertId,
              type: 'curing_ready',
              title: `Ready: ${batch.name || batch.recipeName || 'Batch'}`,
              description: `Curing complete! Ready for testing and sale.`,
              severity: 'high',
              itemId: batch.id,
              autoGenerated: true,
            });
          }
        } else if (daysLeft <= 7) {
          const alertId = `curing_soon_${batch.id}`;
          if (!dismissedAlerts.includes(alertId)) {
            alerts.push({
              id: alertId,
              type: 'curing_soon',
              title: `Almost Ready: ${batch.name || batch.recipeName || 'Batch'}`,
              description: `${daysLeft} day(s) until curing complete.`,
              severity: 'low',
              itemId: batch.id,
              autoGenerated: true,
            });
          }
        }
      }

      // Expiry alerts
      if (batch.soapExpiryDate) {
        const expDate = new Date(batch.soapExpiryDate);
        const daysToExpiry = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));

        if (daysToExpiry <= 30 && daysToExpiry > 0) {
          const alertId = `expiring_${batch.id}`;
          if (!dismissedAlerts.includes(alertId)) {
            alerts.push({
              id: alertId,
              type: 'expiring',
              title: `Expiring: ${batch.name || batch.recipeName || 'Batch'}`,
              description: `Expires in ${daysToExpiry} days. Consider selling or using soon.`,
              severity: daysToExpiry <= 7 ? 'high' : 'medium',
              itemId: batch.id,
              autoGenerated: true,
            });
          }
        }
      }
    });

    return alerts;
  }, [ingredients, batches, dismissedAlerts]);

  // Custom reminder alerts
  const reminderAlerts = useMemo(() => {
    const today = new Date();
    return customReminders
      .filter(r => !r.completed)
      .map(r => {
        const dueDate = r.dueDate ? new Date(r.dueDate) : null;
        const isOverdue = dueDate && dueDate < today;
        const isDueToday = dueDate && dueDate.toDateString() === today.toDateString();
        
        return {
          id: `reminder_${r.id}`,
          type: 'reminder',
          title: r.title,
          description: r.description || (dueDate ? `Due: ${dueDate.toLocaleDateString()}` : ''),
          severity: isOverdue ? 'high' : isDueToday ? 'medium' : 'low',
          dueDate: r.dueDate,
          reminderId: r.id,
          priority: r.priority,
          autoGenerated: false,
        };
      });
  }, [customReminders]);

  // All alerts combined
  const allAlerts = useMemo(() => {
    const combined = [...autoAlerts, ...reminderAlerts];
    // Sort by severity
    const severityOrder = { high: 0, medium: 1, low: 2 };
    return combined.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);
  }, [autoAlerts, reminderAlerts]);

  // Dismiss an alert
  const dismissAlert = (alertId) => {
    setDismissedAlerts(prev => [...prev, alertId]);
  };

  // Reset dismissed alerts
  const resetDismissed = () => {
    setDismissedAlerts([]);
  };

  // Add custom reminder
  const addReminder = async () => {
    if (!newReminder.title.trim()) {
      alert('Please enter a title');
      return;
    }

    try {
      await addUserDoc('reminders', {
        ...newReminder,
        completed: false,
      });
      setNewReminder({ title: '', description: '', dueDate: '', priority: 'medium' });
      setShowAddReminder(false);
    } catch (error) {
      alert('Failed to add reminder: ' + error.message);
    }
  };

  // Mark reminder complete
  const completeReminder = async (reminderId) => {
    try {
      await updateUserDoc('reminders', reminderId, { completed: true });
    } catch (error) {
      console.error('Error completing reminder:', error);
    }
  };

  // Delete reminder
  const deleteReminder = async (reminderId) => {
    if (window.confirm('Delete this reminder?')) {
      try {
        await deleteUserDoc('reminders', reminderId);
      } catch (error) {
        console.error('Error deleting reminder:', error);
      }
    }
  };

  // Send browser notification (for future use with service workers)
  const sendBrowserNotification = useCallback((title, body) => {
    if (notificationPermission === 'granted') {
      new Notification(title, { body, icon: '🧪' });
    }
  }, [notificationPermission]);

  const header = (
    <div className="pageheader borderleft">
      <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'Notifications' }]} />
      <div className="pageheader-row">
        <div className="pageheading">Notifications</div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="sl-page">
        {header}
        <div className="borderleft nt-empty">Loading notifications…</div>
      </div>
    );
  }

  const highPriorityCount = allAlerts.filter(a => a.severity === 'high').length;
  const completedReminders = customReminders.filter(r => r.completed);

  return (
    <div className="sl-page">
      {header}

      <div className="borderleft">
        <div className="nt-toolbar">
          <p className="nt-summary">
            {allAlerts.length === 0
              ? 'Nothing needs your attention.'
              : `${allAlerts.length} notification${allAlerts.length === 1 ? '' : 's'}${highPriorityCount ? `, ${highPriorityCount} urgent` : ''}`}
          </p>
          <div className="nt-actions">
            {dismissedAlerts.length > 0 && (
              <button className="sl-btn sl-btn-secondary" onClick={resetDismissed}>
                Show dismissed ({dismissedAlerts.length})
              </button>
            )}
            <button className="sl-btn sl-btn-primary" onClick={() => setShowAddReminder(true)}>Add reminder</button>
          </div>
        </div>

        {notificationPermission !== 'granted' && (
          <div className="nt-permission">
            <div>
              <strong>Turn on browser notifications</strong>
              <span>Get alerts even when this tab isn’t open.</span>
            </div>
            <button className="sl-arrow-link nt-text-btn" onClick={requestPermission}>Turn on</button>
          </div>
        )}

        {showAddReminder && (
          <div className="nt-backdrop">
            <div className="nt-modal" role="dialog" aria-label="Add reminder">
              <h3>Add reminder</h3>
              <label className="nt-field">
                <span>Title *</span>
                <input
                  type="text"
                  value={newReminder.title}
                  onChange={(e) => setNewReminder(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="What do you need to remember?"
                />
              </label>
              <label className="nt-field">
                <span>Description</span>
                <textarea
                  value={newReminder.description}
                  onChange={(e) => setNewReminder(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Additional details"
                  rows={3}
                />
              </label>
              <div className="nt-field-row">
                <label className="nt-field">
                  <span>Due date</span>
                  <input
                    type="date"
                    value={newReminder.dueDate}
                    onChange={(e) => setNewReminder(prev => ({ ...prev, dueDate: e.target.value }))}
                  />
                </label>
                <label className="nt-field">
                  <span>Priority</span>
                  <select
                    value={newReminder.priority}
                    onChange={(e) => setNewReminder(prev => ({ ...prev, priority: e.target.value }))}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </label>
              </div>
              <div className="nt-modal-actions">
                <button className="sl-btn sl-btn-secondary" onClick={() => setShowAddReminder(false)}>Cancel</button>
                <button className="sl-btn sl-btn-primary" onClick={addReminder}>Add reminder</button>
              </div>
            </div>
          </div>
        )}

        <section className="sl-band nt-band">
          {allAlerts.length === 0 ? (
            <p className="nt-empty"><strong>All clear.</strong> No notifications or reminders right now.</p>
          ) : (
            <ul className="nt-list">
              {allAlerts.map(alert => {
                const typeInfo = NOTIFICATION_TYPES[alert.type] || NOTIFICATION_TYPES.custom;
                return (
                  <li key={alert.id} className={`nt-item nt-item--${alert.severity || 'low'}`}>
                    <div className="nt-item-body">
                      <div className="nt-item-title">
                        {alert.title}
                        {alert.severity === 'high' && <span className="nt-urgent">Urgent</span>}
                      </div>
                      <p>{alert.description}</p>
                      <span className="nt-meta">{typeInfo.label}{alert.autoGenerated && ' · Added automatically'}</span>
                    </div>
                    <div className="nt-item-actions">
                      {alert.reminderId && (
                        <>
                          <button className="sl-btn sl-btn-secondary" onClick={() => completeReminder(alert.reminderId)}>Done</button>
                          <button className="nt-text-btn nt-delete" onClick={() => deleteReminder(alert.reminderId)}>Delete</button>
                        </>
                      )}
                      {alert.autoGenerated && (
                        <button className="sl-btn sl-btn-secondary" onClick={() => dismissAlert(alert.id)}>Dismiss</button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {completedReminders.length > 0 && (
          <section className="sl-band">
            <h2 className="sl-subtitle">Completed reminders</h2>
            <ul className="nt-list">
              {completedReminders.map(r => (
                <li key={r.id} className="nt-item nt-item--done">
                  <span>{r.title}</span>
                  <button className="nt-text-btn nt-delete" onClick={() => deleteReminder(r.id)}>Delete</button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default Notifications;
